import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useUncontrolled } from 'hooks'

import { Box, FormControl, ListSubheader, MenuItem, Select, Stack, Tooltip, Typography, type SelectChangeEvent } from '@mui/material'

import { navHover, FIELD_HEIGHT, FIELD_RADIUS, fieldAccentColor, fieldBorderColor, fieldFill, fieldFocusRing, fieldHoverRing, Input, MENU_ITEM_SX } from '@repo/ui'

import { ChevronDownIcon, SearchIcon, CheckIcon } from 'lucide-react'

export interface SelectFieldProps {
  open?: boolean
  label?: string
  options: SelectItem[]
  /** Selected value. A string in single-select mode, a string[] when `multiple`. */
  value?: string | string[]
  defaultValue?: string | string[]
  onChange?: (value: string | string[]) => void
  disabled?: boolean
  /** Show a search box at the top of the menu to filter options (long lists). */
  search?: boolean
  /** Allow selecting multiple options. Selected rows get a right-aligned check. */
  multiple?: boolean
  /** Placeholder shown in the trigger when nothing is selected. */
  placeholder?: string
  /**
   * A label kept as a PREFIX once a selection exists — `Display as: Chart` — rather than
   * the trigger falling back to a bare value with nothing saying which dimension it
   * belongs to. Mirrors the pattern the Analytics toolbar's Status control uses (label,
   * then values), generalised here since a second call site now wants it (§36).
   *
   * Optional and additive: omitted, the trigger behaves exactly as before — `placeholder`
   * at rest, the bare `MultiSelectSummary` once populated. When set, `placeholder` is
   * still shown bare AT REST (so the control identifies itself even unused), and this
   * becomes the prefix once there is a value to introduce.
   */
  valuePrefix?: string
  /**
   * Replace the TRIGGER's contents entirely — the menu rows are untouched.
   *
   * For a control whose selected value is better shown as something other than its label
   * text: the market Stats filter renders the chosen status as the SAME pill its table
   * rows use, so the control reads as "you are looking at these 20 pending" rather than
   * as a dropdown whose value happens to be a word.
   *
   * Falls back to the built-in rendering when absent, so no existing caller changes. If
   * you supply this, `valuePrefix` and the empty-state placeholder are yours to handle —
   * that is the point of taking over the slot.
   */
  renderTriggerValue?: () => React.ReactNode
  /**
   * Node pinned to the right of the trigger (before the chevron), e.g. a source
   * badge. Non-interactive — pointer events pass through to the select.
   */
  endAdornment?: React.ReactNode
  /**
   * Sticky footer pinned to the bottom of the dropdown, below the scrollable
   * option list (e.g. "View roles" / "Add custom role"). Mirrors the
   * `menuBottomButton` mechanism in `@repo/ui` `MenuButton` (see ViewsDropdown) —
   * not part of the option list, so it's never affected by search filtering.
   */
  footer?: React.ReactNode
}

export type SelectItem = {
  label: string
  value: string
  rightLabel?: string
  group?: string
  /** Optional leading icon, shown left of the label in the menu + trigger. */
  icon?: React.ReactNode
  /**
   * A second line under the label, IN THE MENU ONLY — never in the trigger, which must
   * stay one line at the field's height.
   *
   * For options whose consequence is not evident from the label. The case that added it:
   * the market Source "Kind" field, where "RSS / Atom" and "Social" differ in something
   * the words do not say — the first is fetched on a schedule, the second is never
   * fetched at all. A reader picking blind gets a source that silently does nothing.
   *
   * Prefer this over a `disabledReason`-style tooltip when the text explains what an
   * ENABLED option does; a tooltip is for why something is unavailable, and hover is the
   * wrong affordance for information needed to choose.
   */
  description?: string
  /** Render the option non-selectable (greyed out). */
  disabled?: boolean
  /** Tooltip explaining why the option is disabled (shown on hover). */
  disabledReason?: string
  /** Node pinned to the right of the row (e.g. a source-integration badge). */
  rightAdornment?: React.ReactNode
}

/**
 * A footer row inside a `SelectField` dropdown — "View roles", "Add custom role".
 *
 * Exported because this is the SECOND site to need it (§36: one hand-roll is allowed,
 * the second is a shared component). It lived as a private `FOOTER_ROW_SX` inside
 * `InviteCompanyUserModal`; `BusinessFiguresPage`'s Role filter wants the identical row,
 * and a second private copy is how the two drift into different heights and hovers.
 *
 * Belongs HERE rather than in a styles bucket because it is defined against this
 * component's own menu geometry — 40px against the 44px option rows, the same 8px
 * corner radius, `action.hover` rather than the brand tint the selectable rows use
 * (a footer row is an action, not an option).
 *
 * NOTE the `ViewsDropdown` copy is deliberately NOT folded in: that footer sits in
 * `@repo/ui` `MenuButton`'s `menuBottomButton` slot against 48px rows, so its numbers
 * answer to a different menu.
 */
export const SELECT_FIELD_FOOTER_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  width: '100%',
  px: '12px',
  py: '10px',
  minHeight: 40,
  borderRadius: '8px',
  cursor: 'pointer',
  color: 'text.primary',
  fontSize: '14px',
  '&:hover': { backgroundColor: 'action.hover' },
} as const

const ITEM_HEIGHT = 44
const ITEM_PADDING_TOP = 8
// Height of the sticky search box: the header's own pt 8 + the 48px Input + pb 6.
// Sticky group headers stick at exactly this offset so the two never overlap, so
// any change to that header's vertical padding must be mirrored here.
const SEARCH_HEADER_H = 62
const ITEMS_TO_SHOW = 8
// Breathing room between the last option and the sticky footer's divider. A
// small COSMETIC gap — deliberately not "reserved scroll room sized to the
// footer".
//
// This dropdown's footer regressed four times, and every attempt shared one
// false premise: that a sticky footer overlays the list, so the list must
// reserve extra room (a spacer, or padding) equal to the footer's height for the
// last option to clear it. That is true for an OUT-OF-FLOW footer
// (position:absolute/fixed), which is the pattern the rule was imported from. It
// is false here. The footer is `position: sticky` AND the true last in-flow
// child of `ul.MuiMenu-list` (see PaperProps.sx — the `ul`, not the Paper, is the
// scrolling element). A sticky box still occupies its own box in normal flow, so
// the list's scrollHeight already includes the footer's full height. It overlays
// content only at scroll positions where content actually remains below it; at
// `scrollTop = max` it rests at its natural position with the last option
// directly above it, fully visible. Reserving the footer's height a SECOND time
// double-counts it — which is exactly what the 91px spacer did, opening a 91px
// void between the last option and the footer at the bottom of the scroll.
//
// Measured in-browser to settle it (Settings > Company > Users > Invite > Role,
// scrolled to max): with the 91px spacer, footer flush at 0px, last option clear
// by 91px of empty space; with the spacer zeroed, footer flush at 0px, last
// option clear by 0px. Identical correctness, no void. So the spacer is not
// load-bearing and the runtime footer measurement it required is gone with it.
//
// What IS still load-bearing, and must not be "simplified" back:
//   1. The gap goes BEFORE the footer as an in-flow child, never as
//      `padding-bottom` on `.MuiMenu-list` (see the `hasFooter` branch below).
//      Sticky cannot stick past anything trailing it in its own scrolling
//      container — trailing padding becomes a wall the footer detaches against,
//      resting well short of the scrollport bottom.
//   2. The footer carries NO margin; its lead-in space is padding (see its `sx`).
//      Transparent margin on an opaque sticky box lets rows scrolling underneath
//      paint through the gap above its divider.
const FOOTER_GAP = 8

const getMenuProps = (hasFooter: boolean, hasStickyHeader: boolean) => ({
  // Opening a dropdown must not move the page.
  //
  // MUI treats a Menu as a modal and scroll-LOCKS the document while it is open,
  // flipping `body` to `overflow: hidden`. On a page whose `body` actually scrolls
  // — the signup flow is one — killing that scroll reflows the content underneath,
  // so the field you just clicked JUMPS as the menu appears. Measured on signup step
  // 2: `body` overflow went `hidden auto` -> `hidden` and the select's own top moved
  // from 817px to 778px, a 39px jump, with the menu still opening against the old
  // position.
  //
  // The lock exists to stop the page scrolling behind a full-screen dialog. A select
  // menu is anchored TO the trigger and closes on scroll anyway, so it never needed
  // the lock — it is inherited from Modal, not chosen.
  //
  // Set on the shared field rather than per call site (§31/§47): every SelectField in
  // both apps had this, it just only became obvious on a page long enough to scroll.
  // `Sort.tsx` had already discovered it and fixed its own menu in isolation, which is
  // exactly the drift a shared component exists to prevent.
  disableScrollLock: true,
  // Always drop the menu BELOW the trigger with a real gap (MUI's default overlaps it).
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' } as const,
  transformOrigin: { vertical: 'top', horizontal: 'left' } as const,
  sx: {
    // 8px — the single dropdown gap, the same one `MENU_PAPER_SX` now carries for every
    // menu that spreads it and `filterDropdownPaperSx` carries for the filter panels. It
    // is stated here, on the Menu ROOT, because the block below explains why this one
    // menu deliberately does not spread `MENU_PAPER_SX`. Do NOT also spread the constant
    // onto the paper: the root margin and the paper margin ADD, and the gap becomes 16.
    mt: '8px',
  },
  autoFocus: false,
  MenuListProps: {
    sx: { py: '8px' },
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * ITEMS_TO_SHOW + ITEM_PADDING_TOP,
      width: 250,
    },
    // Concentric radii: the menu corner radius = row radius (8px) + the uniform
    // 8px inset, so the rounded row highlights nest cleanly inside the menu corners
    // instead of poking out. (outer = inner + gap — the shared dropdown convention.)
    //
    // MENU_PAPER_SX is deliberately NOT spread here, and this is the one menu in the
    // app where that is correct. Its total row-to-paper inset already MATCHES the house
    // 8px — the house splits it 4 (paper) + 4 (list), this splits it 0 + 8 — so there is
    // no padding drift to fix. What the spread would additionally bring is `overflowY:
    // auto` on the Paper, which is precisely the arrangement the block below exists to
    // undo: the Paper is `overflow: hidden` + a flex column so `ul.MuiMenu-list` is the
    // scrolling element, which is what makes the sticky search header and sticky footer
    // stick at all. Its `& .MuiMenu-list` padding rule would also outrank the
    // conditional `paddingTop: 0` / `paddingBottom: 0` those two depend on. Four
    // documented regressions live in this file; the inset is already right, so the
    // constant buys nothing here and would cost the sticky machinery.
    // MUI sets an INLINE padding-right: 0 on the list (scrollbar compensation), so
    // !important is required to restore the symmetric right gutter.
    //
    // Sticky-footer fix: by default MUI Menu makes the PAPER the scrolling element
    // (Paper gets `overflow-y: auto` + this `maxHeight`; the inner `ul.MuiMenu-list`
    // is a plain, unclipped block). A `position: sticky` footer rendered as the last
    // child of that `ul` is therefore sticky against an ancestor TWO levels up
    // (Paper) while its own immediate parent (`ul`) never establishes a scrolling
    // box itself — which is exactly the "sticky needs the scrolling element to be
    // the ancestor" trap. Rather than rely on that indirection, make Paper a flex
    // column and hand the scrolling role to `ul` itself (`flex:1; min-height:0;
    // overflow-y:auto`), so the footer's `position:'sticky'` is applied to a child
    // of the element that ACTUALLY scrolls — the textbook-reliable form of sticky.
    sx: {
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      // Paper no longer scrolls itself — `.MuiMenu-list` below does.
      overflow: 'hidden',
      '& .MuiMenu-list': {
        paddingLeft: '8px',
        paddingRight: '8px !important',
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        // When a footer is rendered, it MUST be the true last flow child with
        // ZERO trailing space after it — a sticky element can't stick past
        // content/padding that follows it in its own scrolling container (see
        // the FOOTER_GAP comment above). The baseline `py:'8px'` (MenuListProps
        // above) would otherwise leave 8px of trailing bottom padding after the
        // footer; explicitly zero it out here so nothing follows the footer at
        // all. The equivalent reserved room (so the last real option can still
        // scroll fully clear of the footer) is instead added as an in-flow
        // spacer CHILD immediately before the footer (see the footer render
        // site) — never as padding on this list. Selects without a footer are
        // unaffected (this override only applies when hasFooter).
        ...(hasFooter ? { paddingBottom: 0 } : null),
        // Mirror of the footer rule, for the sticky SEARCH header. The list's
        // own 8px `padding-top` sits ABOVE a `top: 0` sticky child, so rows
        // scrolled up into that strip and appeared above the search box — the
        // list looked like it was escaping its own header.
        //
        // Negative margins on the header do NOT fix this, which is the tempting
        // wrong answer (and was tried): a stuck element is positioned from the
        // scrollport by its `top` inset, so `margin-top` only moves it while it
        // is still in flow. Removing the padding is what actually closes the gap
        // — with no padding above it, there is nothing for a row to occupy.
        // The header restores the inset with its own `padding-top` instead.
        ...(hasStickyHeader ? { paddingTop: 0 } : null),
      },
    },
  },
})

// Design-system row style for menu items: rounded, comfortable padding, neutral
// hover, brand-tinted selected — parity with the shared @repo/ui Select dropdown.
const itemSx = {
  // House per-row convention (MENU_ITEM_SX): 12px on all four sides and a 4px gap
  // between consecutive rows. Was 10/12 with a 2px gap — a fourth set of numbers for
  // what reads as the same row as an ActionDropdown's or a Select's.
  ...MENU_ITEM_SX,
  minHeight: '44px',
  // 8px, NOT the constant's 6. The paper's own radius is 16 because this dropdown is
  // concentric by construction — outer = row radius + the 8px inset (see PaperProps.sx
  // below). Taking 6 here without also moving the paper to 14 would break that
  // relationship, and the paper radius is load-bearing for the sticky header/footer
  // clipping, so the single field is overridden rather than the pair re-derived.
  borderRadius: '8px',
  fontSize: '16px',
  transition: 'background-color 0.15s ease',
  // navChrome for hover, so this matches every other menu.
  '&:hover': {
    backgroundColor: (theme: any) => navHover(theme),
  },
  /*
    ⚠️ SUPPRESSED EXPLICITLY, NOT DELETED — and the difference is the whole bug.

    The first attempt at this simply REMOVED the local `&.Mui-selected` rule, on the
    reasoning that deleting a tint removes the tint. It does not: `theme.ts`'s
    `MuiMenuItem` override sets `backgroundColor: navSelected(theme)` on
    `&.Mui-selected` for every menu item in both apps, so dropping the local rule
    just exposed the theme-level one underneath and the rows stayed tinted. Removing
    an override does not remove styling; it falls back to whatever it was overriding.

    So it is set to `transparent` — an assertion rather than an absence, which is also
    what makes it survive a future change to the theme default.
  */
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-selected:hover': {
    backgroundColor: (theme: any) => navHover(theme),
  },
  /*
    ⚠️ SELECTED IS THE CHECK, AND ONLY THE CHECK — no brand tint.

    This carried `backgroundColor: navSelected(theme)` on `.Mui-selected` as well as the
    right-aligned check rendered below, i.e. TWO signals for one state. Removed so the
    selected row reads the same here as in the Analytics period dropdown, which is a plain
    `MenuItem` + `MENU_ITEM_SX` + a check and therefore never had a tint to begin with.

    The tint is the one to drop rather than the check, for two reasons. It COMPETES WITH
    HOVER: both are a full-row background wash from the same `navChrome` family, so on a
    long list the row under the cursor and the row you picked are the same gesture in the
    same place, and only their alpha separates them. And a tint is ambiguous in this app's
    own vocabulary — §34 fixes tint as "considering" and reserves a persistent mark for
    "chosen", which is precisely the check's job. The check is also positional and survives
    greyscale, which a low-alpha brand wash does not.

    ⚠️ THIS IS NOT YET CONSISTENT APP-WIDE, and the residue is worth knowing: `@repo/ui`
    `Select` still tints AND renders no check, so it is now the third variant rather than
    the second. Aligning it is a visual change to every `Select` in both apps and wants its
    own decision — it is not something to smuggle in behind this one.
  */
}

/**
 * One shared canvas for text measurement, at module scope.
 *
 * A canvas rather than a hidden measuring DOM node because the summary below runs in
 * `useLayoutEffect` on every selection change: a hidden node would need a second layout
 * pass to read back, and every trigger on the page would carry a duplicate of its own
 * label list in the DOM.
 */
let measureCtx: CanvasRenderingContext2D | null = null
const textWidth = (text: string, font: string): number => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')
  if (!measureCtx) return 0
  measureCtx.font = font
  return measureCtx.measureText(text).width
}

/**
 * Canvas metrics and laid-out text agree to well under a pixel for these fonts, but
 * they are not the SAME measurement — so the fit test keeps a small margin rather than
 * comparing exactly. Two px is under one character and is the difference between "+2"
 * sitting comfortably and "+2" being the thing that overflows.
 */
const FIT_MARGIN_PX = 2

/**
 * ── THE MULTI-SELECT TRIGGER SUMMARY: WHOLE LABELS, THEN "+N" ────────────────────────
 *
 * The trigger has to answer "how many filters are applied" at rest. It could not:
 * `renderValue` joined the first two labels and let a CSS ellipsis clip whatever
 * overflowed, so a 143px trigger holding two solutions rendered `Traceability & Logisti…`
 * — a label cut mid-word, no count, and no way to tell one selection from three.
 *
 * The rule this implements: **as many WHOLE labels as fit, then `+N` for the rest.**
 * Never a label cut mid-word while a count would have fitted, and never a bare `+N`
 * with no readable label beside it.
 *
 * ## Why it MEASURES rather than picking a constant
 *
 * The old code took the first two labels regardless of width, which is right for
 * "Legal, Finance" (91px) and absurd for "Sustainability & Compliance, Consumer
 * Engagement" (362px) in the same 143px box. Label widths here span 4x — the taxonomy
 * enums range from "Legal" to "Sustainability & Compliance" — so no fixed count is
 * correct at any trigger width, and the triggers are FLEX (they compress from 177px to
 * 143px as the sidebar opens). The count has to come from the actual box.
 *
 * ## The layout is what guarantees "+N" is never the thing that gets clipped
 *
 * The head span shrinks and ellipsizes; the count span is `flexShrink: 0`. So in the one
 * degenerate case — a single selected label wider than the whole trigger — the reader
 * still gets `Sustainability & Comp… +2` rather than losing the count too. That case is
 * the ONLY one that truncates, and it truncates because no arrangement of that string
 * fits, not because we chose to cut it.
 *
 * ⚠️ `useLayoutEffect`, not `useEffect`: this runs before paint, so the trigger never
 * shows a wrong count for a frame. The `ResizeObserver` handles the trigger changing
 * width underneath a fixed selection (sidebar open/close, window resize); the initial
 * fit does NOT depend on it firing.
 *
 * ## Exported (VRG-528) — §36, the second call site
 *
 * `UserSelectField` needs the identical answer to the identical question: a fixed-width
 * trigger has to say WHICH people are picked, and "first two names" is wrong for the
 * same reason it was wrong here (name widths vary, the trigger is a flex item). It takes
 * only `labels`, so nothing about it is specific to a MUI `Select`; a second copy would
 * be two implementations of one measurement.
 */
export const MultiSelectSummary = ({ labels }: { labels: string[] }) => {
  const hostRef = useRef<HTMLSpanElement | null>(null)
  const [shown, setShown] = useState(labels.length)

  // `labels` is a fresh array every render, so it cannot be a dependency without the
  // effect re-running (and re-subscribing the observer) on every render. The identity
  // that matters is the SELECTION, which the joined string captures; the array itself is
  // read through a ref, so the effect always sees the current one without depending on it.
  //
  // The separator is a space rather than a control character on purpose: two selections
  // that differ only in where the spaces fall cannot occur (the values come from a fixed
  // option set), and a raw NUL in source is unreadable to every tool that touches it.
  const labelsRef = useRef(labels)
  labelsRef.current = labels
  const selectionKey = labels.join(' ')

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host) return undefined

    const fit = () => {
      const current = labelsRef.current
      const available = host.clientWidth
      // A zero-width box means the trigger is not laid out yet (closed menu, first
      // paint). Showing everything is the safe guess, since the ellipsis already covers
      // it, and the observer re-runs this the moment a real width exists.
      if (available <= 0) return
      const font = window.getComputedStyle(host).font

      for (let count = current.length; count >= 1; count -= 1) {
        const head = current.slice(0, count).join(', ')
        const remaining = current.length - count
        const width =
          textWidth(head, font) + (remaining > 0 ? textWidth(` +${remaining}`, font) : 0)
        if (width <= available - FIT_MARGIN_PX) {
          setShown(count)
          return
        }
      }
      // Not even the first label fits. Show it anyway, ellipsized, so the count still has
      // something to qualify: a bare "+3" names no filter at all.
      setShown(1)
    }

    fit()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(fit)
    observer.observe(host)
    return () => observer.disconnect()
  }, [selectionKey])

  const visible = Math.min(shown, labels.length)
  const remaining = labels.length - visible

  return (
    <Box
      component="span"
      ref={hostRef}
      sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}
    >
      <Box
        component="span"
        sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {labels.slice(0, visible).join(', ')}
      </Box>
      {remaining > 0 && (
        // `flexShrink: 0` is the whole point — the count is the part the reader came for,
        // so it is the part that must never be the one clipped.
        <Box component="span" sx={{ flexShrink: 0, pl: 1 }}>
          {`+${remaining}`}
        </Box>
      )}
    </Box>
  )
}

export const SelectField = ({ open, options, onChange, defaultValue = '', value, label, disabled, search = false, multiple = false, placeholder, valuePrefix, renderTriggerValue, endAdornment, footer }: SelectFieldProps) => {
  const [_value, setValue] = useUncontrolled({
    value: value,
    defaultValue: multiple && defaultValue === '' ? ([] as string[]) : defaultValue,
    onChange: onChange,
  })

  const [_open, setOpen] = useUncontrolled({
    value: open,
    defaultValue: false,
  })

  const [searchText, setSearchText] = useState('')

  const internalOnChange = (event: SelectChangeEvent) => {
    setValue(event?.target?.value)
  }

  const shown = search && searchText
    ? options.filter((o) => o.label.toLowerCase().includes(searchText.toLowerCase()))
    : options

  // Selected values, normalized to an array — for the checkmark + trigger summary.
  const selectedVals: string[] = Array.isArray(_value) ? _value : _value ? [_value] : []

  // Multi-select bulk helpers (ported from the integrations worktree).
  const selectAll = () => setValue(options.map((o) => o.value) as any)
  const clearAll = () => setValue([] as any)

  /*
    Right-aligned check on each selected row, in BOTH single and multiple mode.

    ⚠️ It was `multiple &&`, and that had to change in the same edit that removed the
    selected TINT above — not as a separate improvement. In single-select the tint was the
    ONLY indication of which row was chosen, so dropping it while leaving the check gated
    on `multiple` would have left ~33 consumers with a menu that shows no selection at all.

    Single-select gaining the check is also the parity the change is FOR: the Analytics
    period dropdown is single-select and marks its current row with exactly this check and
    no tint.
  */
  const renderRow = (item: SelectItem) => (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        // `flex-start` only when a description makes the row two lines — otherwise the
        // icon and the check would sit against the top of a taller row instead of
        // centred on the label. A row with no description is byte-identical to before.
        alignItems: item.description ? 'flex-start' : 'center',
        gap: 1.5,
      }}
    >
      {item.icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <span>{item.label}</span>
        {item.description && (
          <Typography
            variant="body4"
            color="text.secondary"
            sx={{
              display: 'block',
              // 1.4, not the body default: this is a short clause under its own label,
              // and running-text leading reads as a detached second paragraph.
              lineHeight: 1.4,
              mt: '2px',
              whiteSpace: 'normal',
            }}
          >
            {item.description}
          </Typography>
        )}
      </Box>
      {item.rightLabel != null && (
        <Typography variant="body3" color="text.secondary" sx={{ fontSize: '13px' }}>{item.rightLabel}</Typography>
      )}
      {item.rightAdornment != null && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{item.rightAdornment}</Box>
      )}
      {selectedVals.includes(item.value) && (
        <CheckIcon size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
      )}
    </Box>
  )

  // A menu row, greyed + tooltip-wrapped when the option is disabled.
  const renderMenuItem = (item: SelectItem, key: React.Key) => {
    const node = (
      <MenuItem key={key} value={item.value} disabled={item.disabled} sx={itemSx}>
        {renderRow(item)}
      </MenuItem>
    )
    if (item.disabled && item.disabledReason) {
      // Disabled MUI items don't fire hover — wrap in a span so the tooltip shows.
      return (
        <Tooltip key={key} title={item.disabledReason} placement="right">
          <span style={{ display: 'block' }}>{node}</span>
        </Tooltip>
      )
    }
    return node
  }

  // Only reserve the sticky-footer bottom padding on selects that actually render
  // one — see FOOTER_GAP / getMenuProps above.
  // The sticky header renders for `search` OR `multiple` — see the ListSubheader
  // below; both must zero the list's top padding or the same strip reappears.
  const hasStickyHeader = !!search || !!multiple
  const menuProps = React.useMemo(
    () => getMenuProps(!!footer, hasStickyHeader),
    [footer, hasStickyHeader],
  )

  return (
    <FormControl sx={{ width: '100%', position: 'relative' }}>
      {label && (
        <Typography variant="body3" mb="8px">
          {label}
        </Typography>
      )}


      <Select
        open={_open}
        value={_value as any}
        defaultValue={defaultValue as any}
        multiple={multiple}
        variant="outlined"
        size="medium"
        disabled={disabled}
        displayEmpty
        // The visible label is a Typography above the control; give the combobox the same name.
        SelectDisplayProps={label ? { 'aria-label': label } : undefined}
        renderValue={(selected) => {
          // A caller-supplied trigger wins outright — see `renderTriggerValue`'s note.
          if (renderTriggerValue) return renderTriggerValue()
          const arr: string[] = Array.isArray(selected) ? selected : selected ? [selected as string] : []
          if (arr.length === 0) {
            return <span style={{ opacity: 0.5 }}>{placeholder ?? ''}</span>
          }
          // `valuePrefix` — kept as a LABEL once there is a value, never replaced by it
          // (see the prop's own note). `flexShrink: 0` so a long selection compresses the
          // summary beside it rather than the prefix itself ever being the thing clipped.
          const prefix = valuePrefix ? (
            <Box component="span" sx={{ flexShrink: 0, pr: 1 }}>
              {`${valuePrefix}:`}
            </Box>
          ) : null
          if (!multiple) {
            return (
              <>
                {prefix}
                {options.find((o) => o.value === arr[0])?.label ?? arr[0]}
              </>
            )
          }
          const labels = arr.map((v) => options.find((o) => o.value === v)?.label ?? v)
          // As many WHOLE labels as actually fit this trigger, then "+N" — measured, not
          // a fixed "first two". See MultiSelectSummary for why a constant cannot be right
          // when label widths span 4x and the trigger itself is a flex item.
          if (!prefix) return <MultiSelectSummary labels={labels} />
          return (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
              {prefix}
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                <MultiSelectSummary labels={labels} />
              </Box>
            </Box>
          )
        }}
        IconComponent={() => (
          <ChevronDownIcon
            strokeWidth={1}
            style={{
              position: 'absolute',
              right: 12,
              pointerEvents: 'none',
              width: 20,
              height: 20,
              transform: _open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        )}
        onChange={internalOnChange}
        MenuProps={menuProps}
        onOpen={() => setOpen(true)}
        onClose={() => { setOpen(false); setSearchText('') }}
        sx={(theme) => ({
          // VRG-703 — FIELD_HEIGHT (49), the shared Input's height. This was 47, so a
          // SelectField beside a TextField or Search sat 2-3px short of it.
          height: `${FIELD_HEIGHT}px`,
          // fieldChrome, not local tokens: this notched outline was `primary[100]` while
          // the Input beside it was `grey[200]` — one form, one row, two greys.
          backgroundColor: fieldFill(theme),
          borderRadius: `${FIELD_RADIUS}px`,
          fontSize: '16px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor(theme),
          },
          // HOVER — brand border + a soft glow (`fieldHoverRing`), matching every
          // other field in the design system (§31 — one source, `fieldChrome`,
          // rather than this component's own `theme.palette.brand?.[600] ||
          // '#7F48DF'` literal). Skipped for `disabled`, same as `Input` — a
          // disabled Select cannot be opened, so it should not invite a hover.
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: disabled ? fieldBorderColor(theme) : fieldAccentColor(theme),
          },
          '&:hover': {
            boxShadow: disabled ? 'none' : fieldHoverRing(theme),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldAccentColor(theme),
            // 1px, PINNED — MUI's own `.Mui-focused .MuiOutlinedInput-notchedOutline`
            // rule sets `border-width: 2px`, and this block only ever claimed the
            // COLOUR, so every SelectField in the app doubled its edge the moment it
            // was clicked. Measured on the product form, same row, same moment:
            //
            //   TextField (FilledInput), focused   1px rgb(127, 72, 223)
            //   SelectField (Outlined),  focused   2px rgb(127, 72, 223)
            //
            // A field and the select beside it are one control type at one emphasis
            // (§31), so half a brand edge heavier reads as the select shouting — which
            // is how it was reported, as the dropdown being "so purple" next to
            // everything else. It also shifts the text by a pixel as the border grows.
            //
            // `authFieldChrome` had already found this and pinned 1px locally, with the
            // same note; the shared field never did. That is the §31/§47 drift exactly:
            // one call site fixes the shared component's defect for itself and the other
            // forty inherit it. Fixed here so it cannot recur per call site.
            borderWidth: '1px',
          },
          '&.Mui-focused': {
            boxShadow: fieldFocusRing(theme),
          },
          '& .MuiSelect-select': {
            paddingRight: '40px !important',
            // Never let a multi-select summary overflow / wrap the trigger.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        })}
      >
        {(search || multiple) && (
          <ListSubheader
            sx={{
              bgcolor: 'background.paper',
              lineHeight: 'normal',
              zIndex: 2,
              top: 0,
              // VERTICAL: the list's `padding-top` is zeroed for us (see
              // `hasStickyHeader` in getMenuProps) precisely so nothing sits above
              // this header. This padding puts that inset back, inside the opaque
              // area, where a row cannot get behind it.
              //
              // HORIZONTAL: a genuine bleed. Rows are inset 8px by the list's side
              // padding; without pulling out to the edges the background would stop
              // 8px short and a row's rounded corner would show past it.
              //
              // Longhand on purpose: a `p` shorthand alongside these would win or
              // lose by declaration order depending on how the sx object is merged.
              mx: '-8px',
              px: '14px',
              pt: '8px',
              pb: '6px',
            }}
          >
            {search && (
              <Input
                autoFocus
                fullWidth
                placeholder="Search..."
                value={searchText}
                onChange={(v: string) => setSearchText(v)}
                startAdornment={<SearchIcon size={16} strokeWidth={1} />}
                inputProps={{
                  onKeyDown: (e: any) => { if (e.key !== 'Escape') e.stopPropagation() },
                  onClick: (e: any) => e.stopPropagation(),
                }}
                sx={{ fontSize: 14 }}
              />
            )}
            {multiple && (
              <Stack direction="row" justifyContent="space-between" sx={{ px: '6px', pt: search ? '8px' : '2px', pb: '2px' }}>
                <Typography
                  variant="body3"
                  onClick={(e) => { e.stopPropagation(); selectAll() }}
                  // Neutral white (text.primary), not brand purple — parity with the
                  // Select-All treatment everywhere else (e.g. the SearchSelect picker).
                  sx={{ cursor: 'pointer', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                >
                  Select all
                </Typography>
                <Typography
                  variant="body3"
                  onClick={(e) => { e.stopPropagation(); clearAll() }}
                  sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { textDecoration: 'underline' } }}
                >
                  Clear all
                </Typography>
              </Stack>
            )}
          </ListSubheader>
        )}
        {search && shown.length === 0 && (
          <MenuItem disabled sx={itemSx}>No results</MenuItem>
        )}
        {(() => {
          const hasGroups = shown.some((o) => o.group != null)

          if (!hasGroups) {
            return shown.map((item, index) => renderMenuItem(item, index))
          }

          // Grouped rendering — flat array of [ListSubheader, ...MenuItems] per group
          const groupOrder: string[] = []
          const grouped: Record<string, SelectItem[]> = {}
          for (const item of shown) {
            const g = item.group ?? ''
            if (!grouped[g]) {
              grouped[g] = []
              groupOrder.push(g)
            }
            grouped[g].push(item)
          }

          return groupOrder.flatMap((group) => [
            <ListSubheader
              key={`group-${group}`}
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                lineHeight: '32px',
                px: '12px',
                // Sticky group header — parity with the data-table column-layout menu.
                // Sticks just BELOW the sticky search box (SEARCH_HEADER_H) rather than
                // at top:0, so the two never collide; opaque bg so rows scroll under it.
                position: 'sticky',
                top: search ? SEARCH_HEADER_H : 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
              }}
            >
              {group}
            </ListSubheader>,
            ...grouped[group].map((item, index) => renderMenuItem(item, `${group}-${index}`)),
          ])
        })()}
        {/* Cosmetic gap between the last option and the footer's divider — an
            in-flow, non-interactive child placed immediately BEFORE the footer,
            never padding on `.MuiMenu-list` (see the FOOTER_GAP / PaperProps.sx
            comments: trailing space after a sticky element makes it detach). It
            is NOT reserved room sized to the footer — the sticky footer is an
            in-flow last child and already reserves its own height; see FOOTER_GAP.
            Safe as a plain `Box` child of `Select`: MUI's `SelectInput` clones
            every child with an `onClick` that early-returns unless
            `event.currentTarget.hasAttribute('tabindex')` (see `handleItemClick`
            in `@mui/material/Select/SelectInput.js`) — a bare `Box`/`div` never
            renders a `tabindex` attribute, so the cloned handler is a no-op here,
            exactly as it already is for the search/footer `Box`es above and
            below. */}
        {footer && <Box aria-hidden sx={{ height: `${FOOTER_GAP}px` }} />}
        {/* Sticky footer — pinned to the bottom of the dropdown. It's the true
            last child of `ul.MuiMenu-list` (nothing trails it — see the spacer
            above and the PaperProps.sx `paddingBottom: 0` override), which (per
            the PaperProps.sx comment above) is now the actual scrolling element,
            so `position: sticky` here sticks against its own immediate scrolling
            parent — the same mechanism the sticky search/group `ListSubheader`s
            above rely on (`top: 0` / `top: SEARCH_HEADER_H`). Never part of the
            selectable option set.

            Note this box carries NO margin (its lead-in spacing before the
            divider/content is all padding, `pt: 2`) — deliberately, and this one
            IS load-bearing. A transparent `margin-top` on an opaque, bottom-anchored
            `position: sticky` box sits OUTSIDE its own painted border-box, so at
            any scroll position where the footer is actively "stuck" mid-list,
            whatever option row happens to be scrolled to that exact screen
            coordinate paints straight through that margin gap — a sliver of the
            underlying list bleeding in right above the footer's divider/first
            row, which is what the reported "top of the footer clipped" symptom
            actually was (not literal clipping — content from behind showing
            through the one region of the sticky box that padding/background
            don't cover). Padding is inside the border-box and is painted with
            the footer's opaque background, so it fully occludes anything
            scrolling underneath — using it instead of margin for the same 9px of
            lead-in space (`pt: 2` = 8px + the 1px `borderTop` = 9px, matching the
            previous 4px margin + 1px border + 4px padding) removes the gap
            entirely instead of just resizing it. */}
        {footer && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              // Above the option rows scrolling underneath it (they're unstyled,
              // z-index:auto).
              zIndex: 2,
              // Symmetric 8px gutter. `pb` is NOT optional styling: the
              // `hasFooter` branch zeroes `.MuiMenu-list`'s own `padding-bottom`
              // (trailing space after a sticky element makes it detach), so
              // without a bottom padding here the footer's last row sits flush
              // against the Paper's bottom edge with no breathing room at all.
              // Putting it on the FOOTER instead of the list is what keeps sticky
              // intact — this padding is inside the footer's own border-box, so
              // nothing trails the footer; it is still the true last child.
              // 8px matches the list's baseline `py: '8px'` gutter, so the menu
              // reads with the same inset top and bottom.
              pt: 2,
              pb: 2,
              // Opaque, matching the menu Paper's own background exactly — MuiMenu's
              // `styleOverrides.paper` (packages/ui/theme/theme.ts) doesn't set an
              // explicit backgroundColor, so Paper falls back to MUI's own default of
              // `theme.palette.background.paper` (mode-aware, fully opaque — see the
              // `background: { default, paper }` entries in theme.ts). Resolving it
              // here the same way — rather than a bare CSS `background.paper` string
              // literal or a hardcoded hex — guarantees the footer can never show the
              // scrolling option rows through it, in either mode.
              backgroundColor: (theme) => theme.palette.background.paper,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {footer}
          </Box>
        )}
      </Select>

      {endAdornment && (
        <Box
          sx={{
            position: 'absolute',
            right: 40,
            top: label ? 'auto' : 0,
            bottom: 0,
            height: `${FIELD_HEIGHT}px`,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {endAdornment}
        </Box>
      )}
    </FormControl>
  )
}
